/**
 * HelpdeskKbBrowse — Iter 105.
 * Public KB browse page tied to a widget snippet key.
 * Route: /help/:snippetKey
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Search, BookOpen, ArrowLeft, Loader2, FileText, ExternalLink, Ticket } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

export default function HelpdeskKbBrowse() {
  const { snippetKey } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [docs, setDocs] = useState([]);
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const search = async (query) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/public/widget/${snippetKey}/kb/browse`,
                                  { params: query ? { q: query } : {} });
      setDocs(r.data.docs || []);
      setWorkspaceName(r.data.workspace_name || "Help center");
      setWorkspaceId(r.data.workspace_id || "");
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally { setLoading(false); }
  };
  useEffect(() => { search(""); }, [snippetKey]); // eslint-disable-line

  // Iter 106 — Inject JSON-LD FAQ schema for search-engine deflection.
  useEffect(() => {
    if (!snippetKey) return;
    let mounted = true;
    let scriptEl = null;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/widget/${snippetKey}/kb/faq-jsonld`);
        if (!mounted || !r.data?.mainEntity?.length) return;
        scriptEl = document.createElement("script");
        scriptEl.type = "application/ld+json";
        scriptEl.setAttribute("data-testid", "hd-kb-jsonld");
        scriptEl.textContent = JSON.stringify(r.data);
        document.head.appendChild(scriptEl);
      } catch { /* silent — SEO is best-effort */ }
    })();
    return () => {
      mounted = false;
      if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    };
  }, [snippetKey]);

  // Iter 106 — Update <title> + meta description for SEO.
  useEffect(() => {
    if (workspaceName) {
      document.title = selectedDoc?.title
        ? `${selectedDoc.title} · ${workspaceName} Help Center`
        : `${workspaceName} Help Center`;
    }
    const meta = document.querySelector('meta[name="description"]');
    const desc = selectedDoc
      ? (selectedDoc.body_text || "").slice(0, 155)
      : `Search ${workspaceName || "the"} help center for guides, FAQs, and how-tos.`;
    if (meta) { meta.setAttribute("content", desc); }
    else if (typeof document !== "undefined") {
      const m = document.createElement("meta");
      m.name = "description"; m.content = desc;
      document.head.appendChild(m);
    }
  }, [workspaceName, selectedDoc]);

  const openDoc = async (doc) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/public/widget/${snippetKey}/kb/docs/${doc.id}`);
      setSelectedDoc(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-8 text-center">
          <BookOpen className="w-10 h-10 mx-auto text-slate-300" />
          <h1 className="mt-3 font-bold">Help center not found</h1>
          <p className="text-sm text-slate-500 mt-2">
            The help center you're looking for either doesn't exist or has been disabled.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="hd-kb-browse-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-slate-500" />
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm truncate">{workspaceName} · Help center</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Browse articles</div>
          </div>
          <button onClick={() => nav(workspaceId ? `/client/portal?ws=${workspaceId}` : "/client/portal")} data-testid="hd-kb-contact-btn"
                   className="hidden sm:inline-flex text-xs font-semibold border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 items-center gap-1.5">
            <Ticket className="w-3 h-3" /> Submit a ticket
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6">
        {selectedDoc ? (
          <DocView doc={selectedDoc} onBack={() => setSelectedDoc(null)} />
        ) : (
          <>
            <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && search(q)}
                        placeholder="Search articles by title…"
                        data-testid="hd-kb-search"
                        className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded text-sm bg-white focus:outline-none focus:border-slate-400" />
              </div>
            </div>

            {loading ? (
              <div className="text-center text-sm text-slate-400 py-16">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
              </div>
            ) : docs.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center"
                    data-testid="hd-kb-empty">
                <FileText className="w-8 h-8 mx-auto text-slate-300" />
                <p className="mt-3 font-semibold">No articles found</p>
                <p className="text-sm text-slate-500 mt-1">
                  {q ? "Try different search terms" : "There aren't any public articles yet."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {docs.map((d) => (
                  <button key={d.id} onClick={() => openDoc(d)}
                           data-testid={`hd-kb-article-${d.id}`}
                           className="bg-white border border-slate-200 rounded-lg p-4 text-left hover:border-slate-400 hover:shadow-sm transition-all">
                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{d.title}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1 flex items-center gap-2">
                          <span>{d.kind}</span>
                          {d.chunk_count && <span>· {d.chunk_count} sections</span>}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function DocView({ doc, onBack }) {
  return (
    <div data-testid="hd-kb-doc-view">
      <button onClick={onBack} data-testid="hd-kb-doc-back"
               className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> All articles
      </button>
      <article className="bg-white border border-slate-200 rounded-lg p-6">
        <h1 className="text-xl font-bold">{doc.title}</h1>
        <div className="mt-1 text-[11px] text-slate-400 uppercase tracking-widest flex items-center gap-2">
          <span>{doc.kind}</span>
          {doc.updated_at && <span>· Updated {new Date(doc.updated_at).toLocaleDateString()}</span>}
          {doc.source_url && (
            <a href={doc.source_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 underline hover:text-slate-700">
              <ExternalLink className="w-3 h-3" /> Source
            </a>
          )}
        </div>
        <div className="mt-4 prose prose-sm max-w-none whitespace-pre-wrap text-slate-700 text-[14px] leading-relaxed"
              data-testid="hd-kb-doc-body">
          {doc.body_text || <span className="italic text-slate-400">This article has no content yet.</span>}
        </div>
      </article>
    </div>
  );
}
